/**
 * One shader for the enemy marks, used by two meshes with a uniform switch — so both are one GPU program:
 *  - the overhead layer (uGround 0): per enemy a screen-space plate (role badge, HP bar, two status icons), and the
 *    floating numbers' characters, all a constant size in CSS pixels so they read the same at every zoom;
 *  - the ground layer (uGround 1): a flat contact shadow with a ring in the type's colour under each enemy.
 *
 * Instance layout (16 floats, interleaved; see enemyMarks.ts):
 *   a0 = world anchor xyz, kind (0 plate, 1 character)
 *   a1 = plate: role cell, hp 0–1 (−1 hides the bar), status cell 1, status cell 2 (−1 none) · character: glyph cell
 *   a2 = colour rgb (linear), alpha
 *   a3 = plate: badge radius px, boss 0/1 · character: size px, _, offset px xy · ground: size (world), 0 ground / 1 air / 2 aura
 *
 * The plate stands ABOVE its anchor (the unit's head): the whole plate is lifted in screen space so the bottom of the
 * HP bar clears the head by `PLATE_GAP_PX` at every zoom. It used to be centred on the anchor, which hung the bar and
 * half the badge over the body — in a column of units the bodies were mostly badges.
 */
import { ATLAS_COLS, ATLAS_ROWS } from "./enemyGlyphs";

/** Screen gap (CSS px) between the head anchor and the bottom of the plate. */
export const PLATE_GAP_PX = 2;
/** The HP bar under the badge: its half extents and its centre's drop below the badge's edge (CSS px). */
export const PLATE_BAR = { halfW: 11, halfH: 2, drop: 4 } as const;
/** Status icon radius (CSS px): the two icons sit to the badge's right. */
export const STATUS_ICON_PX = 5.5;
const f = (x: number): string => x.toFixed(2);

export const MARKS_VERTEX = /* glsl */ `
uniform vec2 uPx;
uniform float uGround;
attribute vec4 a0;
attribute vec4 a1;
attribute vec4 a2;
attribute vec4 a3;
varying vec2 vP;
varying vec4 vA1;
varying vec4 vA2;
varying vec4 vA3;
varying float vKind;
void main() {
  vA1 = a1; vA2 = a2; vA3 = a3; vKind = a0.w;
  if (uGround > 0.5) {
    vec3 w = a0.xyz + vec3(position.x, 0.0, -position.y) * a3.x;
    vP = position.xy * 2.0;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
    return;
  }
  vec4 c = projectionMatrix * viewMatrix * vec4(a0.xyz, 1.0);
  vec2 local;
  if (a0.w < 0.5) {
    float R = a3.x;
    float barLow = R + ${f(PLATE_BAR.drop + PLATE_BAR.halfH)};
    vec2 lo = vec2(-max(R + 2.0, ${f(PLATE_BAR.halfW + 1)}), -barLow - 1.0);
    vec2 hi = vec2(R + ${f(STATUS_ICON_PX * 2 + 1.5)}, R + 2.0);
    local = mix(lo, hi, position.xy + 0.5);
    vP = local;
    // the plate's bottom (the bar, or the badge while the bar is hidden at full health) sits just above the head
    local.y += (a1.y >= 0.0 ? barLow : R + 1.0) + ${f(PLATE_GAP_PX)};
  } else {
    local = position.xy * a3.x + a3.zw;
    vP = position.xy + 0.5;
  }
  c.xy += local * uPx * c.w;
  gl_Position = c;
}`;

export const MARKS_FRAGMENT = /* glsl */ `
uniform sampler2D uAtlas;
uniform float uGround;
varying vec2 vP;
varying vec4 vA1;
varying vec4 vA2;
varying vec4 vA3;
varying float vKind;

vec4 glyph(float cell, vec2 uv) {
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return vec4(0.0);
  float cx = mod(cell, ${ATLAS_COLS}.0), cy = floor(cell / ${ATLAS_COLS}.0);
  return texture2D(uAtlas, vec2((cx + uv.x) / ${ATLAS_COLS}.0, 1.0 - (cy + 1.0 - uv.y) / ${ATLAS_ROWS}.0));
}

vec3 statusTint(float cell) {
  if (cell < 8.5) return vec3(0.62, 0.89, 1.0);      // slowed
  if (cell < 9.5) return vec3(1.0, 0.54, 0.23);      // burning
  if (cell < 10.5) return vec3(0.85, 0.87, 0.9);     // armour
  if (cell < 11.5) return vec3(1.0, 0.35, 0.48);     // shred
  if (cell < 12.5) return vec3(1.0, 0.94, 0.48);     // boosted
  // M36 immune shields (enemyGlyphs.ts 40–43): bone, violet, ember, ice
  if (cell > 39.5 && cell < 40.5) return vec3(0.91, 0.84, 0.69);
  if (cell > 40.5 && cell < 41.5) return vec3(0.77, 0.61, 1.0);
  if (cell > 41.5 && cell < 42.5) return vec3(1.0, 0.54, 0.23);
  if (cell > 42.5 && cell < 43.5) return vec3(0.62, 0.91, 1.0);
  return vec3(0.56, 0.7, 1.0);                       // shielded
}

vec4 over(vec4 dst, vec3 rgb, float a) {
  float oa = a + dst.a * (1.0 - a);
  return oa <= 0.0 ? vec4(0.0) : vec4((rgb * a + dst.rgb * dst.a * (1.0 - a)) / oa, oa);
}

vec4 statusIcon(vec4 o, vec2 p, vec2 at, float cell) {
  if (cell < 0.0) return o;
  float r = ${f(STATUS_ICON_PX)}, d = length(p - at);
  float a = 1.0 - smoothstep(r - 0.6, r + 0.6, d);
  if (a <= 0.0) return o;
  o = over(o, vec3(0.07, 0.08, 0.11), a * 0.92);
  vec4 t = glyph(cell, (p - at) / (r * 1.7) + 0.5);
  return over(o, t.rgb * statusTint(cell), t.a * a);
}

void main() {
  vec4 o = vec4(0.0);
  if (uGround > 0.5) {
    float r = length(vP);
    if (vA3.y > 1.5) {                                 // an aura (heal / shield wall): a thin ring, a faint fill
      float band = smoothstep(0.9, 0.95, r) * (1.0 - smoothstep(0.97, 1.0, r));
      o = vec4(vA2.rgb, (band * 0.75 + (1.0 - smoothstep(0.0, 1.0, r)) * 0.08) * vA2.a);
      if (o.a < 0.01) discard;
      gl_FragColor = o;
      return;
    }
    float shadow = (1.0 - smoothstep(0.0, 0.95, r)) * (vA3.y > 0.5 ? 0.22 : 0.4);
    float ring = smoothstep(0.66, 0.72, r) * (1.0 - smoothstep(0.86, 0.93, r));
    o = vec4(vec3(0.0), shadow);
    o = over(o, vA2.rgb, ring * 0.9);
    o.a *= vA2.a;
  } else if (vKind > 0.5) {
    vec4 t = glyph(vA1.x, vP);
    o = vec4(t.rgb * vA2.rgb, t.a * vA2.a);
  } else {
    vec2 p = vP;
    float R = vA3.x;
    bool boss = vA3.y > 0.5;
    if (vA1.y >= 0.0) {
      vec2 q = p - vec2(0.0, -R - ${f(PLATE_BAR.drop)});
      vec2 h = vec2(${f(PLATE_BAR.halfW)}, ${f(PLATE_BAR.halfH)});
      if (abs(q.x) <= h.x && abs(q.y) <= h.y) {
        bool inner = abs(q.x) <= h.x - 1.0 && abs(q.y) <= h.y - 1.0;
        float f = (q.x + h.x - 1.0) / (2.0 * h.x - 2.0);
        vec3 hc = mix(vec3(0.93, 0.22, 0.18), vec3(0.3, 0.86, 0.28), smoothstep(0.25, 0.6, vA1.y));
        o = vec4(inner ? (f <= vA1.y ? hc : vec3(0.12)) : vec3(0.04), 0.95);
      }
    }
    float d = length(p);
    float rim = boss ? 2.5 : 1.5;
    float aOut = 1.0 - smoothstep(R + 1.0, R + 2.0, d);
    if (aOut > 0.0) {
      float inDisc = 1.0 - smoothstep(R - rim, R - rim + 1.0, d);
      vec3 c = mix(boss ? vec3(1.0, 0.72, 0.16) : vec3(0.05, 0.055, 0.08), vA2.rgb, inDisc);
      vec4 t = glyph(vA1.x, p / (R * 1.62) + 0.5);
      c = mix(c, t.rgb, t.a * inDisc);
      o = over(o, c, aOut);
    }
    o = statusIcon(o, p, vec2(R + ${f(STATUS_ICON_PX)}, R * 0.42), vA1.z);
    o = statusIcon(o, p, vec2(R + ${f(STATUS_ICON_PX)}, -R * 0.62 - 1.0), vA1.w);
    o.a *= vA2.a;
  }
  if (o.a < 0.01) discard;
  gl_FragColor = o;
}`;
